import { useCallback, useMemo, useState } from "react";
import { TabHeader } from "components/TabHeader";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";
import { useDebounceFunction } from "newui/hooks/useDebounceFunction";
import { PreferenceMatrix } from "./components/PreferenceMatrix";
import { DigestTimingCard } from "./components/DigestTimingCard";
import { useNotificationTypes } from "./hooks/useNotificationTypes";
import { notificationsLang } from "./lang/en";
import type { NotificationScopeSelection, NotificationScopeType } from "./types/Notifications.type";
import { useProjectFilterOptions } from "./hooks/useProjectTypeOptions";

const RFX_ID_DEBOUNCE_MS = 400;

export interface NotificationSettingsPageProps {
	/** Path prop is consumed by @reach/router; not used directly. */
	path?: string;
}

const SCOPE_TYPE_OPTIONS: { label: string; value: NotificationScopeType }[] = [
	{ label: notificationsLang.scopeGlobal, value: "global" },
	{ label: notificationsLang.scopeProjectType, value: "project_type" },
	{ label: notificationsLang.scopeProject, value: "project" },
	{ label: notificationsLang.scopeRfx, value: "rfx" },
];

export const NotificationSettingsPage = (_props: NotificationSettingsPageProps) => {
	const [scopeType, setScopeType] = useState<NotificationScopeType>("global");
	const [scopeId, setScopeId] = useState<number | undefined>(undefined);
	const [rfxIdInput, setRfxIdInput] = useState<string>("");

	const scope: NotificationScopeSelection = useMemo(
		() => ({ scopeType, scopeId: scopeType === "global" ? undefined : scopeId }),
		[scopeType, scopeId]
	);

	const { data: types, isLoading, isError, refetch } = useNotificationTypes(scope);
	const { data: filterOptions, isLoading: isLoadingProjectOptions } = useProjectFilterOptions(
		scopeType === "project_type" || scopeType === "project"
	);

	// The RFx-id field is free text, so every keystroke ("5", "51", "519")
	// would otherwise derive a new scopeId and fire a request for each
	// intermediate (very likely non-existent) id. Debounce deriving scopeId
	// from the raw input so only the settled value is queried.
	const debouncedSetScopeIdFromRfxInput = useDebounceFunction((value: string) => {
		const parsed = Number(value);
		setScopeId(Number.isFinite(parsed) && value !== "" ? parsed : undefined);
	}, RFX_ID_DEBOUNCE_MS);

	const handleScopeTypeChange = useCallback(
		(nextScopeType: NotificationScopeType) => {
			// Cancel any in-flight debounced RFx-id resolution — otherwise it can
			// fire after the switch and stomp the scopeId this reset just cleared.
			debouncedSetScopeIdFromRfxInput.cancel();
			setScopeType(nextScopeType);
			setScopeId(undefined);
			setRfxIdInput("");
		},
		[debouncedSetScopeIdFromRfxInput]
	);

	const handleRetry = useCallback(() => {
		refetch();
	}, [refetch]);

	return (
		<div data-testid="notification-settings-page">
			<TabHeader heading={notificationsLang.pageHeading} description="" />

			<div className="mt-4 flex flex-wrap items-end gap-4" data-testid="notification-scope-selector">
				<SelectWithLabel
					name="notification-scope-type"
					label={notificationsLang.scopeSelectorLabel}
					testId="scope-type-select"
					options={SCOPE_TYPE_OPTIONS}
					value={SCOPE_TYPE_OPTIONS.find((option) => option.value === scopeType)}
					width="220px"
					onChange={(option) => {
						if (option && !Array.isArray(option)) {
							handleScopeTypeChange(option.value as NotificationScopeType);
						}
					}}
				/>

				{scopeType === "project_type" && (
					<SelectWithLabel
						name="notification-scope-project-type"
						label={notificationsLang.projectTypePickerLabel}
						testId="scope-project-type-select"
						options={(filterOptions?.projectTypes ?? []).map((option) => ({ label: option.name, value: option.id }))}
						isLoading={isLoadingProjectOptions}
						value={scopeId}
						width="220px"
						onChange={(option) => {
							if (option && !Array.isArray(option)) {
								setScopeId(option.value as number);
							}
						}}
					/>
				)}

				{scopeType === "project" && (
					<SelectWithLabel
						name="notification-scope-project"
						label={notificationsLang.projectPickerLabel}
						testId="scope-project-select"
						options={(filterOptions?.projects ?? []).map((option) => ({ label: option.name, value: option.id }))}
						isLoading={isLoadingProjectOptions}
						value={scopeId}
						width="220px"
						onChange={(option) => {
							if (option && !Array.isArray(option)) {
								setScopeId(option.value as number);
							}
						}}
					/>
				)}

				{scopeType === "rfx" && (
					<TextInputWithLabel
						label={notificationsLang.rfxPickerLabel}
						placeholder={notificationsLang.rfxPickerPlaceholder}
						testId="scope-rfx-input"
						value={rfxIdInput}
						onChange={(e) => {
							const nextValue = e.target.value;
							setRfxIdInput(nextValue);
							debouncedSetScopeIdFromRfxInput(nextValue);
						}}
					/>
				)}
			</div>

			<div className="mt-5">
				<PreferenceMatrix
					types={types}
					isLoading={isLoading}
					error={isError}
					onRetry={handleRetry}
					scope={scope}
				/>
			</div>

			{scopeType === "global" && <DigestTimingCard />}
		</div>
	);
};
